import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  Play, Pause, RefreshCw, Smartphone, Monitor,
  DollarSign, Clock, Cpu, Activity, RotateCcw, Zap, Server,
  AlertCircle, GitBranch, Layers, Globe, HardDrive,
  Trash2, Terminal, XCircle, Cast, Battery, Thermometer
} from 'lucide-react';
import axios from 'axios';
import { getLocalProfilePicUrl } from '../config/imageUrls';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`padding: 20px; display: flex; flex-direction: column; gap: 20px;`;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
`;
const Title = styled.h1`font-size: 22px; font-weight: 800; color: var(--text-primary); display: flex; align-items: center; gap: 10px;`;
const RefreshBtn = styled.button`
  padding: 8px 14px; border-radius: 8px; border: 1px solid var(--border);
  background: var(--surface); color: var(--text-secondary); font-size: 12px; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  &:hover { border-color: var(--primary); color: var(--primary); }
`;

const StatsGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 12px;`;

const StatCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 12px;
  padding: 16px; display: flex; flex-direction: column; gap: 6px;
`;
const StatLabel = styled.div`font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: .5px; display: flex; align-items: center; gap: 6px;`;
const StatValue = styled.div`font-size: 26px; font-weight: 900; color: ${p => p.$color || 'var(--text-primary)'};`;
const StatSub = styled.div`font-size: 11px; color: var(--text-muted);`;

const ProcessTable = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 12px;
  overflow: hidden;
`;
const PRow = styled.div`
  display: grid; grid-template-columns: 30px 42px 1fr 150px 110px 120px 90px 80px 70px;
  padding: 10px 16px; align-items: center; gap: 8px;
  border-bottom: 1px solid var(--border);
  font-size: 12px; color: var(--text-secondary);
  &:last-child { border-bottom: none; }
  &:first-child { font-weight: 700; color: var(--text-muted); text-transform: uppercase; font-size: 10px; letter-spacing: .5px; background: var(--surface); }
`;
const ProfilePic = styled.img`
  width: 34px; height: 34px; border-radius: 50%; object-fit: cover;
  border: 2px solid var(--border); flex-shrink: 0;
`;
const ProfilePicFallback = styled.div`
  width: 34px; height: 34px; border-radius: 50%;
  background: var(--surface); border: 2px solid var(--border);
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: var(--text-muted);
`;

const WS_COLORS = ['#a591ef', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#6366f1'];
const PBadge = styled.span`
  padding: 3px 8px; border-radius: 6px; font-size: 10px; font-weight: 700;
  background: ${p => p.$color ? `${p.$color}18` : 'var(--surface)'};
  color: ${p => p.$color || 'var(--text-muted)'};
`;
const PUsername = styled.span`font-weight: 700; color: var(--primary);`;
const PDot = styled.span`width: 8px; height: 8px; border-radius: 50%; background: ${p => p.$color || 'var(--success)'};`;
const DeviceBadge = styled.button`
  padding: 4px 10px; border-radius: 8px; border: 1px solid var(--border);
  background: var(--surface); color: var(--text-primary); font-size: 11px; font-weight: 700;
  cursor: pointer; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap;
  transition: all .15s;
  &:hover { border-color: var(--primary); background: rgba(165,145,239,.08); color: var(--primary); }
`;

const RestarterGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px;`;
const RestarterCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 10px;
  padding: 14px; display: flex; align-items: center; gap: 10px;
`;
const RestarterIcon = styled.div`
  width: 36px; height: 36px; border-radius: 8px;
  background: rgba(245,158,11,.12); color: #f59e0b;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;
const RestarterInfo = styled.div`flex: 1;
  .name { font-size: 13px; font-weight: 700; color: var(--text-primary); }
  .meta { font-size: 10px; color: var(--text-muted); margin-top: 2px; }
`;
const RestarterBtn = styled.button`
  padding: 6px 10px; border-radius: 6px; border: 1px solid rgba(245,158,11,.3);
  background: rgba(245,158,11,.08); color: #f59e0b; font-size: 10px; font-weight: 700;
  cursor: pointer; display: flex; align-items: center; gap: 4px; white-space: nowrap;
  transition: all .15s;
  &:hover { background: rgba(245,158,11,.2); border-color: #f59e0b; }
  &:disabled { opacity: .4; cursor: not-allowed; }
`;

const CostGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 12px;`;
const CostCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 12px; padding: 16px;
`;
const CostLabel = styled.div`font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 6px;`;
const CostValue = styled.div`font-size: 22px; font-weight: 900; color: ${p => p.$color || 'var(--text-primary)'};`;
const CostSub = styled.div`font-size: 10px; color: var(--text-muted); margin-top: 4px;`;

const CostToggles = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px;
`;
const CostToggle = styled.label`
  display: flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 8px;
  background: ${p => p.$active ? 'rgba(165,145,239,.12)' : 'var(--surface)'};
  border: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--border)'};
  cursor: pointer; font-size: 11px; font-weight: 600; color: ${p => p.$active ? 'var(--primary)' : 'var(--text-muted)'};
  transition: all .15s; user-select: none;
  &:hover { border-color: var(--primary); }
  input { accent-color: var(--primary); width: 14px; height: 14px; cursor: pointer; }
`;

const EmptyMsg = styled.div`
  padding: 24px; text-align: center; color: var(--text-muted); font-size: 13px;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
`;

/* ── Layout: Two-column + Collapsible Sections ── */
const TwoColLayout = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  @media (max-width: 1200px) { grid-template-columns: 1fr; }
`;
const SectionPanel = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 12px;
  overflow: hidden; display: flex; flex-direction: column;
`;
const SectionPanelHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; cursor: pointer; user-select: none;
  background: var(--surface); border-bottom: 1px solid var(--card-border);
  transition: background .15s;
  &:hover { background: var(--surface-hover, rgba(255,255,255,.04)); }
`;
const SectionPanelTitle = styled.div`
  font-size: 14px; font-weight: 800; color: var(--text-primary);
  display: flex; align-items: center; gap: 8px;
`;
const SectionPanelBadge = styled.span`
  font-size: 10px; font-weight: 700; color: var(--text-muted);
  background: var(--card-bg); padding: 2px 8px; border-radius: 8px;
`;
const SectionPanelBody = styled.div`
  padding: 14px 16px; display: flex; flex-direction: column; gap: 14px;
  ${p => p.$collapsed ? 'display: none;' : ''}
`;
const SubSectionLabel = styled.div`
  font-size: 11px; font-weight: 700; color: var(--text-secondary);
  text-transform: uppercase; letter-spacing: .6px;
  display: flex; align-items: center; gap: 6px;
  padding-bottom: 6px; border-bottom: 1px solid var(--border);
`;
const ChevronIcon = styled.span`
  font-size: 10px; color: var(--text-muted); transition: transform .2s;
  transform: ${p => p.$open ? 'rotate(90deg)' : 'rotate(0deg)'};
`;
const TwoColInner = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
  @media (max-width: 1200px) { grid-template-columns: 1fr; }
`;

const WsBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 16px; border-radius: 10px;
  background: var(--card-bg); border: 1px solid var(--card-border);
`;
const WsToggleGroup = styled.div`
  display: flex; border-radius: 8px; overflow: hidden;
  border: 1px solid var(--card-border); background: var(--surface);
`;
const WsToggleBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 6px 14px; border: none; cursor: pointer;
  font-size: 11px; font-weight: 700; transition: all .15s;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'var(--text-muted)'};
  &:hover { background: ${p => p.$active ? 'var(--primary)' : 'var(--surface-hover)'}; }
`;
const WsDot = styled.span`
  width: 7px; height: 7px; border-radius: 50%;
  background: ${p => p.$online ? 'var(--success)' : 'var(--error)'};
  flex-shrink: 0;
`;
const WsLabel = styled.span`font-size: 11px; font-weight: 700; color: var(--text-muted);`;

const SysInfoCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 12px;
  padding: 16px; display: flex; flex-direction: column; gap: 10px;
`;
const SysMetric = styled.div`
  background: var(--surface); border-radius: 8px; padding: 8px 12px;
`;
const SysMetricHead = styled.div`
  display: flex; align-items: center; gap: 6px; margin-bottom: 4px;
`;
const SysMetricLabel = styled.span`font-size: 10px; font-weight: 700; color: var(--text-muted); text-transform: uppercase;`;
const SysMetricValue = styled.span`margin-left: auto; font-size: 13px; font-weight: 800;`;
const SysMetricSub = styled.div`font-size: 10px; color: var(--text-secondary);`;
const SysMetricMeta = styled.div`font-size: 9px; color: var(--text-muted);`;
const SysProgressBar = styled.div`height: 3px; border-radius: 2px; background: var(--border); margin-top: 4px;`;
const SysProgressFill = styled.div`height: 100%; border-radius: 2px; transition: width .5s;`;

const ToolsGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px;`;
const ToolBtn = styled.button`
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px; border-radius: 10px; cursor: pointer;
  background: var(--card-bg); border: 1px solid var(--card-border);
  color: var(--text-primary); font-size: 12px; font-weight: 700;
  transition: all .15s;
  &:hover { border-color: ${p => p.$color || 'var(--primary)'}; background: ${p => p.$color ? p.$color + '12' : 'rgba(165,145,239,.08)'}; }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const ToolIcon = styled.div`
  width: 36px; height: 36px; border-radius: 8px;
  background: ${p => p.$color ? p.$color + '18' : 'rgba(165,145,239,.12)'};
  color: ${p => p.$color || 'var(--primary)'};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;
const ToolInfo = styled.div`flex: 1; text-align: left;
  .name { font-size: 12px; font-weight: 700; color: var(--text-primary); }
  .desc { font-size: 10px; color: var(--text-muted); margin-top: 2px; }
`;
const ToolResult = styled.div`
  background: var(--surface); border-radius: 8px; padding: 10px 14px;
  font-size: 11px; color: var(--text-secondary); font-family: 'JetBrains Mono', monospace;
  white-space: pre-wrap; max-height: 120px; overflow-y: auto;
`;

/* ═══════════════════════════════════════════════
   COST CONSTANTS — modèle réaliste
   Samsung A10: 0.5W branché 100% idle, 3W script actif
   PC Fixe: AMD Ryzen 5 PRO 4650G (65W TDP, iGPU Radeon, 12 cores, 16GB RAM)
     → charge ~65W, idle ~25W
   Raspberry Pi 4: 5W constant
   Internet: 8€/forfait × nombre de devices restarter
   ═══════════════════════════════════════════════ */
const ELEC_RATE = 0.2276; // €/kWh (tarif réglementé EDF 2025)
const FORFAIT_MOBILE = 8; // €/mois par forfait SIM (1 par restarter)

const POWER_PROFILES = {
  phone: { activeW: 3, idleW: 0.5, defaultActiveH: 4, label: '📱 Samsung A10' },
  raspberry: { activeW: 5, idleW: 5, defaultActiveH: 24, label: '🍓 Raspberry Pi' },
  desktop: { activeW: 65, idleW: 25, defaultActiveH: 14, label: '🖥️ Ryzen 5 PRO 4650G' },
  laptop: { activeW: 45, idleW: 8, defaultActiveH: 10, label: '💻 PC Portable' },
};

const dailyKwh = (prof, activeH) => {
  const idleH = 24 - activeH;
  return ((prof.activeW * activeH) + (prof.idleW * idleH)) / 1000;
};

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */
const ControlCenter = ({ onSelectDevice, setMirrorOpen }) => {
  const [processes, setProcesses] = useState([]);
  const [devices, setDevices] = useState([]);
  const [workstations, setWorkstations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0); // force re-render for live durations
  const [activeWorkstation, setActiveWorkstation] = useState(() => {
    return localStorage.getItem('impulse_activeWorkstation') || 'local';
  });
  const [wsInfo, setWsInfo] = useState(null);
  const [toolResult, setToolResult] = useState(null);
  const [toolLoading, setToolLoading] = useState(false);
  const [restartingDevice, setRestartingDevice] = useState(null);
  const [geminiStats, setGeminiStats] = useState(null);
  const [powerToday, setPowerToday] = useState([]);

  // Section collapse state — persisted in localStorage
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const saved = localStorage.getItem('cc_collapsed_sections');
      if (saved) return JSON.parse(saved);
    } catch { }
    return { activity: false, infra: false, costs: false };
  });
  const toggleSection = (key) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('cc_collapsed_sections', JSON.stringify(next));
      return next;
    });
  };

  // Modular cost toggles — persisted in localStorage
  const [costToggles, setCostToggles] = useState(() => {
    try {
      const saved = localStorage.getItem('cost_toggles');
      if (saved) return JSON.parse(saved);
    } catch { }
    return { phones: true, forfaits: true, desktop: true, laptop: true, raspberry: true };
  });
  const updateCostToggle = (key) => {
    setCostToggles(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('cost_toggles', JSON.stringify(next));
      return next;
    });
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [procRes, devRes, wsRes] = await Promise.all([
        fetch('/api/processes/all').then(r => r.json()).catch(() => ({ processes: [] })),
        fetch('/api/devices').then(r => r.json()).catch(() => ({ devices: [] })),
        fetch('/api/workstations').then(r => r.json()).catch(() => ({ workstations: [] })),
      ]);
      setProcesses(procRes.processes || []);
      const devList = Array.isArray(devRes.devices) ? devRes.devices : Array.isArray(devRes) ? devRes : [];
      setDevices(devList);
      setWorkstations(wsRes.workstations || []);
    } catch { } finally { setLoading(false); }
  }, []);

  const fetchWsInfo = useCallback(async () => {
    try {
      if (!activeWorkstation || activeWorkstation === 'local' || activeWorkstation === 'all') {
        const r = await axios.get('/api/workstation-info');
        setWsInfo(r.data);
      } else {
        const r = await axios.post(`/api/workstations/${activeWorkstation}/api-proxy`, {
          method: 'GET', path: '/api/workstation-info'
        });
        if (r.data.success && r.data.data) setWsInfo(r.data.data);
        else setWsInfo(null);
      }
    } catch { setWsInfo(null); }
  }, [activeWorkstation]);

  useEffect(() => {
    localStorage.setItem('impulse_activeWorkstation', activeWorkstation);
  }, [activeWorkstation]);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 10000);
    const tickIv = setInterval(() => setTick(t => t + 1), 1000);
    return () => { clearInterval(iv); clearInterval(tickIv); };
  }, [fetchAll]);

  useEffect(() => {
    fetchWsInfo();
    const wsIv = setInterval(fetchWsInfo, 60000);
    return () => clearInterval(wsIv);
  }, [fetchWsInfo]);

  // Fetch Gemini usage stats
  const fetchGeminiStats = useCallback(async () => {
    try {
      const r = await fetch('/api/gemini/usage-stats?days=30');
      const data = await r.json();
      if (data.success) setGeminiStats(data);
    } catch { }
  }, []);

  useEffect(() => {
    fetchGeminiStats();
    const gIv = setInterval(fetchGeminiStats, 60000);
    return () => clearInterval(gIv);
  }, [fetchGeminiStats]);

  // Fetch live battery/power stats
  const fetchPowerToday = useCallback(async () => {
    try {
      const r = await axios.get('/api/workstation-info/power-today');
      if (r.data.success) setPowerToday(r.data.data || []);
    } catch { setPowerToday([]); }
  }, []);

  useEffect(() => {
    fetchPowerToday();
    const pIv = setInterval(fetchPowerToday, 5 * 60 * 1000);
    return () => clearInterval(pIv);
  }, [fetchPowerToday]);

  // Fetch live actions for running scripts
  const fetchScriptActions = useCallback(async (procs) => {
    const running = procs.filter(p => p.status === 'running' && (p.account_id || p.instagram_username));
    if (running.length === 0) { setScriptActions({}); return; }
    const results = {};
    await Promise.all(running.map(async (p) => {
      const key = p.account_id || p.instagram_username;
      try {
        const r = await fetch(`/api/live-actions/${key}`);
        const data = await r.json();
        if (data) results[key] = data;
      } catch { }
    }));
    setScriptActions(results);
  }, []);

  useEffect(() => {
    if (processes.length > 0) fetchScriptActions(processes);
    const iv = setInterval(() => { if (processes.length > 0) fetchScriptActions(processes); }, 15000);
    return () => clearInterval(iv);
  }, [processes, fetchScriptActions]);

  // Derived stats
  const connectedDevices = devices.filter(d => d.isConnected);
  const restarterDevices = devices.filter(d => d.is_restarter);
  const workerDevices = connectedDevices.filter(d => !d.is_restarter);
  const runningProcesses = processes.filter(p => p.status === 'running');
  const connectedWs = workstations.filter(w => w.live_connected);

  // Cost calculations — realistic active/idle model
  const workerPhones = connectedDevices.filter(d => !d.is_restarter);
  const totalPhones = workerPhones.length;
  const pp = POWER_PROFILES;
  const phoneKwhEach = dailyKwh(pp.phone, pp.phone.defaultActiveH);
  const phoneKwh = costToggles.phones ? totalPhones * phoneKwhEach : 0;

  const allWsForCost = workstations.filter(w => !w.is_current);
  const wsDetails = allWsForCost.map(ws => {
    const name = (ws.name || '').toLowerCase();
    const type = name.includes('raspberry') ? 'raspberry' : name.includes('portable') ? 'laptop' : 'desktop';
    const prof = pp[type];
    const kwh = dailyKwh(prof, prof.defaultActiveH);
    const enabled = costToggles[type] !== false;
    return { ws, type, prof, kwh, enabled };
  });
  const wsKwh = wsDetails.filter(d => d.enabled).reduce((s, d) => s + d.kwh, 0);
  const dailyTotalKwh = phoneKwh + wsKwh;
  const dailyCostElec = dailyTotalKwh * ELEC_RATE;

  // Internet: 8€/forfait × nombre de restarter devices
  const restarterCount = restarterDevices.length;
  const monthlyInternet = costToggles.forfaits ? restarterCount * FORFAIT_MOBILE : 0;
  const dailyCostInternet = monthlyInternet / 30;

  const dailyCostTotal = dailyCostElec + dailyCostInternet;
  const monthlyCostElec = dailyCostElec * 30;
  const monthlyCost = dailyCostTotal * 30;
  const phoneAvgW = ((pp.phone.activeW * pp.phone.defaultActiveH) + (pp.phone.idleW * (24 - pp.phone.defaultActiveH))) / 24;
  const phoneWhPerDay = phoneAvgW * 24;

  const [restartingAll, setRestartingAll] = useState(false);
  const [scriptActions, setScriptActions] = useState({}); // { account_id: { likes, follows, ... } }
  const [restartHistory, setRestartHistory] = useState([]);

  // Fetch restart history
  const fetchRestartHistory = useCallback(async () => {
    try {
      const r = await axios.get('/api/restart-history?limit=10');
      setRestartHistory(r.data?.history || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchRestartHistory();
    const iv = setInterval(fetchRestartHistory, 15000);
    return () => clearInterval(iv);
  }, [fetchRestartHistory]);

  // Restart connection handler
  const handleRestartConnection = async (deviceId) => {
    const device = devices.find(d => d.device_id === deviceId);
    const deviceName = device?.device_name || deviceId;
    setRestartingDevice(deviceId);
    try {
      // Log start to history
      axios.post('/api/restart-history', { device_id: deviceId, device_name: deviceName, status: 'running', workstation_name: device?.workstation_name }).catch(() => { });
      await axios.post('/api/phone-cast/script', { device_id: deviceId, script: 'restart-connection' });
      setToolResult(`✅ Restart connexion envoyé à ${deviceName}`);
    } catch (e) {
      setToolResult(`❌ Restart échoué pour ${deviceName}: ${e.response?.data?.error || e.message}`);
    } finally {
      setRestartingDevice(null);
      setTimeout(fetchRestartHistory, 2000);
    }
  };

  // Restart ALL connected restarter devices sequentially
  const handleRestartAll = async () => {
    const online = restarterDevices.filter(d => d.isConnected);
    if (online.length === 0) return;
    setRestartingAll(true);
    let ok = 0, fail = 0;
    for (const d of online) {
      setRestartingDevice(d.device_id);
      try {
        await axios.post('/api/phone-cast/script', { device_id: d.device_id, script: 'restart-connection' });
        ok++;
      } catch { fail++; }
      await new Promise(r => setTimeout(r, 500));
    }
    setRestartingDevice(null);
    setRestartingAll(false);
    setToolResult(`✅ Restart terminé : ${ok} réussi(s), ${fail} échoué(s) sur ${online.length} devices`);
  };

  const formatDuration = (startTime) => {
    if (!startTime) return '-';
    const ms = Date.now() - new Date(startTime).getTime();
    if (ms < 0) return '-';
    const secs = Math.floor(ms / 1000) % 60;
    const mins = Math.floor(ms / 60000) % 60;
    const hrs = Math.floor(ms / 3600000);
    if (hrs > 0) return `${hrs}h${String(mins).padStart(2, '0')}m${String(secs).padStart(2, '0')}s`;
    return `${mins}m${String(secs).padStart(2, '0')}s`;
  };

  const formatTime = (startTime) => {
    if (!startTime) return '-';
    return new Date(startTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  // Build workstation color map
  const wsColorMap = {};
  workstations.forEach((ws, i) => { wsColorMap[ws.id] = WS_COLORS[i % WS_COLORS.length]; });
  // Also map by name
  workstations.forEach((ws, i) => { if (ws.name) wsColorMap[ws.name] = WS_COLORS[i % WS_COLORS.length]; });

  // Pre-compute process groupings for Activity section
  const identified = runningProcesses.filter(p => p.instagram_username || p.username || p.device_id);
  const unidentified = runningProcesses.filter(p => !p.instagram_username && !p.username && !p.device_id);
  const unidByWs = {};
  unidentified.forEach(p => {
    const ws = p.workstation_name || 'Inconnu';
    if (!unidByWs[ws]) unidByWs[ws] = { count: 0, pids: [], wsColor: wsColorMap[p.workstation_id] || wsColorMap[p.workstation_name] || '#a591ef' };
    unidByWs[ws].count++;
    unidByWs[ws].pids.push(p.pid);
  });
  const scriptsWithActions = runningProcesses.filter(p => p.instagram_username || p.username);

  return (
    <Page>
      {/* ═══ HEADER ═══ */}
      <Header>
        <Title><Activity size={22} /> Centre de Contrôle</Title>
        <RefreshBtn onClick={() => { fetchAll(); fetchWsInfo(); }} disabled={loading}>
          <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
          {loading ? 'Chargement...' : 'Rafraîchir'}
        </RefreshBtn>
      </Header>

      {/* ═══ WORKSTATION SELECTOR ═══ */}
      <WsBar>
        <Server size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
        <WsLabel>Workstation :</WsLabel>
        <WsToggleGroup>
          <WsToggleBtn $active={activeWorkstation === 'all'} onClick={() => setActiveWorkstation('all')}>
            <Layers size={12} /> Tous
          </WsToggleBtn>
          <WsToggleBtn $active={activeWorkstation === 'local'} onClick={() => setActiveWorkstation('local')}>
            <Monitor size={12} /> Ce PC
          </WsToggleBtn>
          {workstations.filter(w => !w.is_current).map(w => (
            <WsToggleBtn key={w.id} $active={activeWorkstation === w.id.toString()} onClick={() => setActiveWorkstation(w.id.toString())}>
              <WsDot $online={w.live_connected} />
              {w.name}
              {w.device_count > 0 && <span style={{ opacity: .5, fontSize: 9 }}>({w.device_count})</span>}
            </WsToggleBtn>
          ))}
        </WsToggleGroup>
        {activeWorkstation !== 'all' && activeWorkstation !== 'local' && (
          <WsLabel style={{ color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Globe size={10} /> Contrôle distant via WebSocket
          </WsLabel>
        )}
      </WsBar>

      {/* ═══ KPI SUMMARY ROW ═══ */}
      <StatsGrid>
        <StatCard>
          <StatLabel><Play size={12} /> Scripts actifs</StatLabel>
          <StatValue $color="var(--success)">{runningProcesses.length}</StatValue>
          <StatSub>sur {connectedDevices.length} devices connectés</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel><Smartphone size={12} /> Workers</StatLabel>
          <StatValue>{workerDevices.length}</StatValue>
          <StatSub>{connectedDevices.length} connectés / {devices.length} total</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel><RotateCcw size={12} /> Restarters</StatLabel>
          <StatValue $color="#f59e0b">{restarterDevices.length}</StatValue>
          <StatSub>{restarterDevices.filter(d => d.isConnected).length} en ligne</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel><Server size={12} /> Workstations</StatLabel>
          <StatValue>{connectedWs.length}</StatValue>
          <StatSub>{workstations.length} configurées</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel><DollarSign size={12} /> Coût / jour</StatLabel>
          <StatValue $color="#ef4444">{dailyCostTotal.toFixed(3)} €</StatValue>
          <StatSub>élec {dailyCostElec.toFixed(3)}€ + internet {dailyCostInternet.toFixed(2)}€</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel><DollarSign size={12} /> Coût / mois</StatLabel>
          <StatValue $color="#ef4444">{monthlyCost.toFixed(2)} €</StatValue>
          <StatSub>élec {monthlyCostElec.toFixed(2)}€ + forfaits {monthlyInternet.toFixed(0)}€ ({restarterCount}×8€)</StatSub>
        </StatCard>
      </StatsGrid>

      {/* ═══ TWO-COLUMN LAYOUT: ACTIVITÉ | INFRASTRUCTURE ═══ */}
      <TwoColLayout>

        {/* ── LEFT COLUMN: Activité ── */}
        <SectionPanel>
          <SectionPanelHeader onClick={() => toggleSection('activity')}>
            <SectionPanelTitle>
              <Cpu size={16} /> Activité
              <SectionPanelBadge>{runningProcesses.length} processus</SectionPanelBadge>
            </SectionPanelTitle>
            <ChevronIcon $open={!collapsedSections.activity}>▶</ChevronIcon>
          </SectionPanelHeader>
          <SectionPanelBody $collapsed={collapsedSections.activity}>

            {/* Processus en cours */}
            <SubSectionLabel><Cpu size={12} /> Processus en cours ({identified.length}{unidentified.length > 0 ? ` + ${unidentified.length} non id.` : ''})</SubSectionLabel>
            <ProcessTable>
              <PRow>
                <span></span><span></span><span>Compte</span><span>Type</span><span>Device</span><span>Workstation</span><span>Début</span><span>Durée</span><span>PID</span>
              </PRow>
              {identified.length === 0 && unidentified.length === 0 ? (
                <EmptyMsg><Pause size={20} /> Aucun script en cours</EmptyMsg>
              ) : (<>
                {identified.map((p, i) => {
                  const igUser = p.instagram_username || p.username;
                  const picUrl = igUser ? getLocalProfilePicUrl(igUser) : null;
                  const scriptLabel = p.scriptType || 'script';
                  const SCRIPT_COLORS = { interact: '#22c55e', unfollow: '#f59e0b', scrape: '#3b82f6', dm: '#8b5cf6', restart: '#ef4444', gmail: '#ea4335', tiktok: '#000000', script: '#6b7280' };
                  const scriptColor = SCRIPT_COLORS[scriptLabel] || '#6b7280';
                  const PLATFORM_META = { instagram: { label: 'IG', color: '#E1306C' }, gmail: { label: 'Gmail', color: '#ea4335' }, tiktok: { label: 'TT', color: '#000' } };
                  const plat = PLATFORM_META[p.platform] || PLATFORM_META.instagram;
                  const wfLetter = p.workflow_letter || p.workflow_id || null;
                  const wsColor = wsColorMap[p.workstation_id] || wsColorMap[p.workstation_name] || '#a591ef';
                  return (
                    <PRow key={p.id || i}>
                      <PDot $color="var(--success)" />
                      {picUrl ? (
                        <ProfilePic src={picUrl} alt="" onError={e => { if (!e.target.dataset.fb) { e.target.dataset.fb = '1'; e.target.src = `https://unavatar.io/instagram/${igUser}`; } else e.target.style.display = 'none'; }} />
                      ) : (
                        <ProfilePicFallback>{(igUser || '?')[0].toUpperCase()}</ProfilePicFallback>
                      )}
                      <div>
                        <PUsername>@{igUser || '?'}</PUsername>
                        {p.is_restarter && <PBadge $color="#ef4444" style={{ marginLeft: 4 }}>restarter</PBadge>}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                        <PBadge $color={plat.color}>{plat.label}</PBadge>
                        <PBadge $color={scriptColor}>{scriptLabel}</PBadge>
                        {wfLetter && <PBadge $color="#6366f1"><GitBranch size={8} style={{ marginRight: 2 }} />{wfLetter}</PBadge>}
                      </div>
                      <div>
                        <DeviceBadge
                          title={`Sélectionner ${p.device_name || p.device_id} dans la sidebar`}
                          onClick={() => {
                            const dev = devices.find(d => d.device_id === p.device_id);
                            if (dev && onSelectDevice) onSelectDevice(dev);
                          }}
                        >
                          <Smartphone size={10} />
                          {p.device_name || p.device_id || '-'}
                        </DeviceBadge>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: wsColor, flexShrink: 0, boxShadow: `0 0 6px ${wsColor}60` }} />
                        <span style={{ fontWeight: 600 }}>{p.workstation_name || (p.source === 'local' ? 'Local' : '?')}</span>
                      </div>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>{formatTime(p.startTime)}</span>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, color: 'var(--success)' }}>{formatDuration(p.startTime)}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{p.pid || '-'}</span>
                    </PRow>
                  );
                })}
                {Object.entries(unidByWs).map(([wsName, info]) => (
                  <PRow key={`unid_${wsName}`} style={{ background: 'rgba(245,158,11,.04)', borderLeft: `3px solid ${info.wsColor}` }}>
                    <PDot $color="#f59e0b" />
                    <ProfilePicFallback style={{ background: 'rgba(245,158,11,.1)', color: '#f59e0b' }}>
                      <AlertCircle size={14} />
                    </ProfilePicFallback>
                    <div style={{ gridColumn: 'span 2' }}>
                      <span style={{ fontWeight: 700, color: '#f59e0b', fontSize: 12 }}>{info.count} scripts détectés</span>
                      <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2 }}>cmd.exe récupérés — pull le code sur {wsName} pour identifier</div>
                    </div>
                    <span></span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: info.wsColor, flexShrink: 0, boxShadow: `0 0 6px ${info.wsColor}60` }} />
                      <span style={{ fontWeight: 600 }}>{wsName}</span>
                    </div>
                    <span></span><span></span>
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'var(--text-muted)' }}>{info.pids.slice(0, 3).join(', ')}{info.pids.length > 3 ? '...' : ''}</span>
                  </PRow>
                ))}
              </>)}
            </ProcessTable>

            {/* Détails des scripts actifs */}
            {scriptsWithActions.length > 0 && (<>
              <SubSectionLabel><Zap size={12} /> Détails scripts actifs</SubSectionLabel>
              <ProcessTable>
                <PRow>
                  <span></span><span></span><span>Compte</span><span>Script</span><span>Durée</span>
                  <span>❤️ Likes</span><span>➕ Follows</span><span>👤 Users</span><span>⚡ Act/min</span>
                </PRow>
                {scriptsWithActions.map((p, i) => {
                  const igUser = p.instagram_username || p.username;
                  const key = p.account_id || igUser;
                  const actions = scriptActions[key] || {};
                  const likes = actions.likes_count || actions.total_likes || 0;
                  const follows = actions.follows_count || actions.total_follows || 0;
                  const users = actions.unique_users || actions.total_unique_users || 0;
                  const totalActs = likes + follows + (actions.unfollows_count || 0) + (actions.story_views || 0);
                  const startMs = p.startTime ? Date.now() - new Date(p.startTime).getTime() : 0;
                  const mins = startMs > 0 ? startMs / 60000 : 1;
                  const actPerMin = mins > 0 ? (totalActs / mins).toFixed(1) : '0';
                  const picUrl = igUser ? getLocalProfilePicUrl(igUser) : null;
                  const SCRIPT_COLORS = { interact: '#22c55e', unfollow: '#f59e0b', scrape: '#3b82f6', dm: '#8b5cf6', restart: '#ef4444' };
                  const scriptColor = SCRIPT_COLORS[p.scriptType] || '#6b7280';
                  return (
                    <PRow key={`sa_${p.id || i}`}>
                      <PDot $color="var(--success)" />
                      {picUrl ? (
                        <ProfilePic src={picUrl} alt="" onError={e => { if (!e.target.dataset.fb) { e.target.dataset.fb = '1'; e.target.src = `https://unavatar.io/instagram/${igUser}`; } else e.target.style.display = 'none'; }} />
                      ) : (
                        <ProfilePicFallback>{(igUser || '?')[0].toUpperCase()}</ProfilePicFallback>
                      )}
                      <PUsername>@{igUser}</PUsername>
                      <PBadge $color={scriptColor}>{p.scriptType || 'script'}</PBadge>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: 'var(--success)' }}>{formatDuration(p.startTime)}</span>
                      <span style={{ fontWeight: 700, color: 'var(--accent, #ff6b9d)' }}>{likes}</span>
                      <span style={{ fontWeight: 700, color: '#22c55e' }}>{follows}</span>
                      <span style={{ fontWeight: 700, color: 'var(--primary)' }}>{users}</span>
                      <span style={{ fontWeight: 700, color: 'var(--info, #3b82f6)' }}>{actPerMin}</span>
                    </PRow>
                  );
                })}
              </ProcessTable>
            </>)}

          </SectionPanelBody>
        </SectionPanel>

        {/* ── RIGHT COLUMN: Infrastructure ── */}
        <SectionPanel>
          <SectionPanelHeader onClick={() => toggleSection('infra')}>
            <SectionPanelTitle>
              <Server size={16} /> Infrastructure
              <SectionPanelBadge>{connectedWs.length} WS · {connectedDevices.length} devices</SectionPanelBadge>
            </SectionPanelTitle>
            <ChevronIcon $open={!collapsedSections.infra}>▶</ChevronIcon>
          </SectionPanelHeader>
          <SectionPanelBody $collapsed={collapsedSections.infra}>

            {/* System Info */}
            {wsInfo && activeWorkstation !== 'all' && (<>
              <SubSectionLabel><HardDrive size={12} /> Système ({wsInfo.workstation || wsInfo.hostname})</SubSectionLabel>
              <SysInfoCard style={{ border: 'none', padding: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <Server size={14} style={{ color: 'var(--primary)' }} />
                  <span style={{ fontWeight: 800, fontSize: 13, color: 'var(--text-primary)' }}>{wsInfo.workstation || wsInfo.hostname}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>{wsInfo.os} • {wsInfo.arch}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <SysMetric>
                    <SysMetricHead>
                      <Cpu size={12} style={{ color: 'var(--primary)' }} />
                      <SysMetricLabel>CPU</SysMetricLabel>
                      <SysMetricValue style={{ color: wsInfo.cpu?.usage > 80 ? 'var(--error)' : wsInfo.cpu?.usage > 50 ? '#f59e0b' : 'var(--success)' }}>{wsInfo.cpu?.usage || 0}%</SysMetricValue>
                    </SysMetricHead>
                    <SysMetricSub>{(wsInfo.cpu?.model || 'N/A').substring(0, 35)}</SysMetricSub>
                    <SysMetricMeta>{wsInfo.cpu?.cores || 0} cores • {wsInfo.cpu?.speed || 0} MHz</SysMetricMeta>
                    <SysProgressBar><SysProgressFill style={{ width: `${wsInfo.cpu?.usage || 0}%`, background: wsInfo.cpu?.usage > 80 ? 'var(--error)' : wsInfo.cpu?.usage > 50 ? '#f59e0b' : 'var(--success)' }} /></SysProgressBar>
                  </SysMetric>
                  <SysMetric>
                    <SysMetricHead>
                      <Activity size={12} style={{ color: '#3b82f6' }} />
                      <SysMetricLabel>RAM</SysMetricLabel>
                      <SysMetricValue style={{ color: wsInfo.memory?.percent > 85 ? 'var(--error)' : wsInfo.memory?.percent > 60 ? '#f59e0b' : 'var(--success)' }}>{wsInfo.memory?.percent || 0}%</SysMetricValue>
                    </SysMetricHead>
                    <SysMetricSub>{wsInfo.memory?.usedGB || 0} / {wsInfo.memory?.totalGB || 0} GB</SysMetricSub>
                    <SysMetricMeta>{wsInfo.memory?.freeGB || 0} GB libre</SysMetricMeta>
                    <SysProgressBar><SysProgressFill style={{ width: `${wsInfo.memory?.percent || 0}%`, background: wsInfo.memory?.percent > 85 ? 'var(--error)' : wsInfo.memory?.percent > 60 ? '#f59e0b' : '#3b82f6' }} /></SysProgressBar>
                  </SysMetric>
                  <SysMetric>
                    <SysMetricHead>
                      <HardDrive size={12} style={{ color: '#22c55e' }} />
                      <SysMetricLabel>DISK</SysMetricLabel>
                      {wsInfo.disk?.[0] && <SysMetricValue style={{ color: wsInfo.disk[0].percent > 90 ? 'var(--error)' : wsInfo.disk[0].percent > 70 ? '#f59e0b' : 'var(--success)' }}>{wsInfo.disk[0].percent}%</SysMetricValue>}
                    </SysMetricHead>
                    {wsInfo.disk?.[0] ? (<>
                      <SysMetricSub>{wsInfo.disk[0].usedGB} / {wsInfo.disk[0].totalGB} GB ({wsInfo.disk[0].mount})</SysMetricSub>
                      <SysMetricMeta>{wsInfo.disk[0].freeGB} GB libre</SysMetricMeta>
                      <SysProgressBar><SysProgressFill style={{ width: `${wsInfo.disk[0].percent}%`, background: wsInfo.disk[0].percent > 90 ? 'var(--error)' : '#22c55e' }} /></SysProgressBar>
                    </>) : <SysMetricMeta>N/A</SysMetricMeta>}
                  </SysMetric>
                  <SysMetric>
                    <SysMetricHead>
                      <Globe size={12} style={{ color: '#8b5cf6' }} />
                      <SysMetricLabel>RÉSEAU</SysMetricLabel>
                    </SysMetricHead>
                    <SysMetricSub>IP: {wsInfo.network?.ip || 'N/A'}</SysMetricSub>
                    <SysMetricMeta>{wsInfo.network?.interface || ''} • MAC: {(wsInfo.network?.mac || 'N/A').substring(0, 17)}</SysMetricMeta>
                  </SysMetric>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 10, color: 'var(--text-muted)', padding: '4px 0 0' }}>
                  <span>Uptime: <b style={{ color: 'var(--text-secondary)' }}>{wsInfo.uptime || 'N/A'}</b></span>
                  <span>Node: <b style={{ color: 'var(--text-secondary)' }}>{wsInfo.nodeVersion || 'N/A'}</b></span>
                  {wsInfo.temperature != null && <span>Temp: <b style={{ color: wsInfo.temperature > 70 ? 'var(--error)' : 'var(--text-secondary)' }}>{wsInfo.temperature}°C</b></span>}
                  {wsInfo.gpu?.name && wsInfo.gpu.name !== 'N/A' && <span>GPU: <b style={{ color: 'var(--text-secondary)' }}>{wsInfo.gpu.name.substring(0, 30)}</b></span>}
                  {wsInfo.loadAvg && <span>Load: <b style={{ color: 'var(--text-secondary)' }}>{wsInfo.loadAvg.map(l => l.toFixed(2)).join(' ')}</b></span>}
                </div>
              </SysInfoCard>
            </>)}

            {/* Devices Restarter */}
            <SubSectionLabel>
              <RotateCcw size={12} /> Devices Restarter ({restarterDevices.length})
              {restarterDevices.filter(d => d.isConnected).length > 0 && (
                <RestarterBtn
                  disabled={restartingAll}
                  onClick={handleRestartAll}
                  style={{ padding: '4px 10px', fontSize: 10, marginLeft: 'auto' }}
                  title={`Restart les ${restarterDevices.filter(d => d.isConnected).length} devices en ligne`}
                >
                  {restartingAll ? <RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={10} />}
                  {restartingAll ? 'En cours...' : `Restart tous (${restarterDevices.filter(d => d.isConnected).length})`}
                </RestarterBtn>
              )}
            </SubSectionLabel>
            {restarterDevices.length === 0 ? (
              <EmptyMsg style={{ padding: 16 }}><AlertCircle size={16} /> Aucun restarter</EmptyMsg>
            ) : (
              <RestarterGrid>
                {restarterDevices.map(d => (
                  <RestarterCard key={d.device_id}>
                    <RestarterIcon><RotateCcw size={18} /></RestarterIcon>
                    <RestarterInfo>
                      <div className="name">{d.device_name || d.device_id}</div>
                      <div className="meta">
                        {d.isConnected ? '🟢 En ligne' : '🔴 Hors ligne'}
                        {d.workflow ? ` · WF: ${d.workflow}` : ''}
                      </div>
                    </RestarterInfo>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {d.isConnected && onSelectDevice && (
                        <button
                          onClick={() => { onSelectDevice({ device_id: d.device_id, device_name: d.device_name || d.device_id, isConnected: true }); if (setMirrorOpen) setMirrorOpen(true); }}
                          title="Screen Mirror"
                          style={{ background: 'rgba(165,145,239,.12)', border: '1px solid rgba(165,145,239,.3)', borderRadius: 6, padding: '6px 8px', color: 'var(--primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700 }}
                        >
                          <Cast size={11} />
                        </button>
                      )}
                      <RestarterBtn
                        disabled={!d.isConnected || restartingDevice === d.device_id}
                        onClick={() => handleRestartConnection(d.device_id)}
                        title="Restart connexion via ce device"
                      >
                        {restartingDevice === d.device_id ? <RefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={11} />}
                        {restartingDevice === d.device_id ? 'En cours...' : 'Restart'}
                      </RestarterBtn>
                    </div>
                  </RestarterCard>
                ))}
              </RestarterGrid>
            )}

            {/* Restart History */}
            {restartHistory.length > 0 && (
              <>
                <SubSectionLabel><Clock size={12} /> Historique des restarts</SubSectionLabel>
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, overflow: 'hidden' }}>
                  {restartHistory.slice(0, 8).map((rh, i) => (
                    <div key={rh.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', fontSize: 11, borderBottom: i < Math.min(restartHistory.length, 8) - 1 ? '1px solid var(--border)' : 'none' }}>
                      <RotateCcw size={10} style={{ color: rh.status === 'success' ? '#22c55e' : rh.status === 'error' ? '#ef4444' : '#f59e0b', flexShrink: 0 }} />
                      <span style={{ fontWeight: 700, color: 'var(--text-primary)', minWidth: 50 }}>{rh.device_name || rh.device_id}</span>
                      <span style={{ color: 'var(--text-muted)', flex: 1, fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {rh.ip_before && rh.ip_after ? `${rh.ip_before} → ${rh.ip_after}` : rh.ip_after ? `IP: ${rh.ip_after}` : ''}
                      </span>
                      <span style={{ fontSize: 9, color: rh.status === 'success' ? '#22c55e' : rh.status === 'error' ? '#ef4444' : '#f59e0b', fontWeight: 700, flexShrink: 0 }}>
                        {rh.status === 'success' ? '✓ OK' : rh.status === 'error' ? '✗ Erreur' : '● En cours'}
                      </span>
                      {rh.duration_ms && <span style={{ fontSize: 9, color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>{(rh.duration_ms / 1000).toFixed(0)}s</span>}
                      <span style={{ fontSize: 9, fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-muted)', flexShrink: 0 }}>
                        {rh.created_at ? new Date(rh.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Outils de contrôle */}
            <SubSectionLabel><Terminal size={12} /> Outils de contrôle</SubSectionLabel>
            <ToolsGrid>
              <ToolBtn $color="#ef4444" disabled={toolLoading} onClick={async () => {
                const wsId = activeWorkstation === 'all' || activeWorkstation === 'local' ? 'local' : activeWorkstation;
                setToolLoading(true); setToolResult(null);
                try {
                  const r = await axios.post(`/api/workstations/${wsId}/taskkill`, { processName: 'python' });
                  setToolResult(`✅ ${r.data.message || r.data.output || 'Done'}`);
                  fetchAll();
                } catch (e) { setToolResult(`❌ ${e.response?.data?.error || e.message}`); }
                finally { setToolLoading(false); }
              }}>
                <ToolIcon $color="#ef4444"><XCircle size={18} /></ToolIcon>
                <ToolInfo>
                  <div className="name">Taskkill Python</div>
                  <div className="desc">Tuer tous les processus Python</div>
                </ToolInfo>
              </ToolBtn>
              <ToolBtn $color="#f59e0b" disabled={toolLoading} onClick={async () => {
                const wsId = activeWorkstation === 'all' || activeWorkstation === 'local' ? 'local' : activeWorkstation;
                setToolLoading(true); setToolResult(null);
                try {
                  const r = await axios.post(`/api/workstations/${wsId}/kill-terminals`);
                  setToolResult(`✅ ${r.data.message || 'Done'}`);
                  fetchAll();
                } catch (e) { setToolResult(`❌ ${e.response?.data?.error || e.message}`); }
                finally { setToolLoading(false); }
              }}>
                <ToolIcon $color="#f59e0b"><Terminal size={18} /></ToolIcon>
                <ToolInfo>
                  <div className="name">Fermer terminaux</div>
                  <div className="desc">Fermer les CMD (sauf serveur)</div>
                </ToolInfo>
              </ToolBtn>
              <ToolBtn $color="#8b5cf6" disabled={toolLoading} onClick={async () => {
                const wsId = activeWorkstation === 'all' || activeWorkstation === 'local' ? 'local' : activeWorkstation;
                setToolLoading(true); setToolResult(null);
                try {
                  const r = await axios.post(`/api/workstations/${wsId}/clear-processes`);
                  setToolResult(`✅ ${r.data.message || 'Cleared'}`);
                  fetchAll();
                } catch (e) { setToolResult(`❌ ${e.response?.data?.error || e.message}`); }
                finally { setToolLoading(false); }
              }}>
                <ToolIcon $color="#8b5cf6"><Trash2 size={18} /></ToolIcon>
                <ToolInfo>
                  <div className="name">Vider processus</div>
                  <div className="desc">Supprimer les entrées fantômes</div>
                </ToolInfo>
              </ToolBtn>
            </ToolsGrid>
            {toolResult && <ToolResult>{toolResult}</ToolResult>}

          </SectionPanelBody>
        </SectionPanel>

      </TwoColLayout>

      {/* ═══ FULL-WIDTH: COÛTS & API ═══ */}
      <SectionPanel>
        <SectionPanelHeader onClick={() => toggleSection('costs')}>
          <SectionPanelTitle>
            <DollarSign size={16} /> Coûts & API
            <SectionPanelBadge>{monthlyCost.toFixed(2)} €/mois</SectionPanelBadge>
          </SectionPanelTitle>
          <ChevronIcon $open={!collapsedSections.costs}>▶</ChevronIcon>
        </SectionPanelHeader>
        <SectionPanelBody $collapsed={collapsedSections.costs}>

          <TwoColInner>
            {/* Left: Gemini API */}
            <div>
              <SubSectionLabel><span style={{ fontSize: 13 }}>🤖</span> Gemini API — Suivi des coûts</SubSectionLabel>
              {geminiStats ? (<>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                  <div style={{ background: 'var(--surface)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Aujourd'hui</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#8b5cf6' }}>{Number(geminiStats.today?.requests || 0)}</div>
                    <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>requêtes</div>
                  </div>
                  <div style={{ background: 'var(--surface)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Coût aujourd'hui</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#ef4444' }}>€{Number(geminiStats.today?.cost_eur || 0).toFixed(4)}</div>
                    <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{Number(geminiStats.today?.tokens || 0).toLocaleString()} tok</div>
                  </div>
                  <div style={{ background: 'var(--surface)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Total 30j</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#f59e0b' }}>{Number(geminiStats.totals?.total_requests || 0)}</div>
                    <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{Number(geminiStats.totals?.successful_requests || 0)} OK / {Number(geminiStats.totals?.failed_requests || 0)} err</div>
                  </div>
                  <div style={{ background: 'var(--surface)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Coût total 30j</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#ef4444' }}>€{Number(geminiStats.totals?.total_cost_eur || 0).toFixed(4)}</div>
                    <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{Number(geminiStats.totals?.total_tokens || 0).toLocaleString()} tok</div>
                  </div>
                </div>
                {geminiStats.by_model && geminiStats.by_model.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>Par modèle</div>
                    {geminiStats.by_model.map((m, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, padding: '3px 0', borderBottom: '1px solid var(--border)' }}>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{m.model || 'unknown'}</span>
                        <span style={{ display: 'flex', gap: 12, color: 'var(--text-muted)' }}>
                          <span>{Number(m.requests)} req</span>
                          <span>{Number(m.tokens || 0).toLocaleString()} tok</span>
                          <span style={{ color: '#ef4444', fontWeight: 700 }}>€{Number(m.cost_eur || 0).toFixed(4)}</span>
                          <span>~{Math.round(Number(m.avg_duration_ms || 0))}ms</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {geminiStats.by_type && geminiStats.by_type.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>Par type de requête</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {geminiStats.by_type.map((t, i) => (
                        <div key={i} style={{ background: 'var(--surface)', borderRadius: 6, padding: '4px 10px', fontSize: 10 }}>
                          <span style={{ fontWeight: 700, color: '#8b5cf6' }}>{t.request_type}</span>
                          <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>{Number(t.requests)} req</span>
                          <span style={{ color: '#ef4444', marginLeft: 6, fontWeight: 600 }}>€{Number(t.cost_eur || 0).toFixed(4)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>) : (
                <EmptyMsg style={{ padding: 16 }}><AlertCircle size={16} /> Aucune donnée Gemini</EmptyMsg>
              )}
            </div>

            {/* Right: Consommation électrique */}
            <div>
              <SubSectionLabel><Zap size={12} /> Consommation électrique</SubSectionLabel>

              {/* Live battery/power stats */}
              {powerToday.length > 0 && (
                <div style={{ background: 'var(--surface)', borderRadius: 8, padding: '10px 12px', marginTop: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Battery size={11} /> Batteries aujourd'hui
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {powerToday.map(p => {
                      const dev = devices.find(d => d.device_id === p.device_id);
                      const label = dev?.device_name || (p.device_id || '').substring(0, 8);
                      return (
                        <div key={p.device_id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, background: 'var(--card-bg)', borderRadius: 6, padding: '4px 8px', border: '1px solid var(--border)' }}>
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{label}</span>
                          <span style={{ color: p.avg_battery > 50 ? 'var(--success)' : p.avg_battery > 20 ? '#f59e0b' : 'var(--error)', fontWeight: 600 }}>
                            <Battery size={10} /> {p.avg_battery}%
                          </span>
                          {p.avg_temperature > 0 && (
                            <span style={{ color: p.avg_temperature > 40 ? 'var(--error)' : 'var(--text-muted)' }}>
                              <Thermometer size={9} /> {p.avg_temperature}°C
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <CostToggles style={{ marginTop: 10 }}>
                <CostToggle $active={costToggles.phones}>
                  <input type="checkbox" checked={costToggles.phones} onChange={() => updateCostToggle('phones')} />
                  📱 Phones ({totalPhones})
                </CostToggle>
                {wsDetails.map(({ ws, type }) => (
                  <CostToggle key={ws.id} $active={costToggles[type] !== false}>
                    <input type="checkbox" checked={costToggles[type] !== false} onChange={() => updateCostToggle(type)} />
                    {POWER_PROFILES[type].label} {ws.name}
                  </CostToggle>
                ))}
                <CostToggle $active={costToggles.forfaits}>
                  <input type="checkbox" checked={costToggles.forfaits} onChange={() => updateCostToggle('forfaits')} />
                  📡 Forfaits ({restarterCount})
                </CostToggle>
              </CostToggles>
              <CostGrid style={{ gridTemplateColumns: '1fr' }}>
                {costToggles.phones && (
                  <CostCard>
                    <CostLabel>📱 Phones workers ({totalPhones})</CostLabel>
                    <CostValue>{(phoneKwh * ELEC_RATE).toFixed(3)} €/jour</CostValue>
                    <CostSub>
                      {pp.phone.activeW}W scripts × {pp.phone.defaultActiveH}h + {pp.phone.idleW}W branché 100% × {24 - pp.phone.defaultActiveH}h
                      <br />{phoneWhPerDay.toFixed(0)}Wh/tél/j = {(phoneKwhEach * 1000).toFixed(0)}Wh · {totalPhones} tél = {(phoneKwh * 1000).toFixed(0)}Wh/j
                      <br />{(phoneKwh * ELEC_RATE * 30).toFixed(2)} €/mois
                    </CostSub>
                  </CostCard>
                )}
                {wsDetails.filter(d => d.enabled).map(({ ws, prof, kwh, type }) => {
                  const avgW = ((prof.activeW * prof.defaultActiveH) + (prof.idleW * (24 - prof.defaultActiveH))) / 24;
                  return (
                    <CostCard key={ws.id}>
                      <CostLabel>{prof.label} {ws.name || 'Workstation'}</CostLabel>
                      <CostValue>{(kwh * ELEC_RATE).toFixed(3)} €/jour</CostValue>
                      <CostSub>
                        {prof.activeW}W charge × {prof.defaultActiveH}h + {prof.idleW}W idle × {24 - prof.defaultActiveH}h
                        <br />Moy: {avgW.toFixed(0)}W · {(kwh * 1000).toFixed(0)}Wh/j · {(kwh * ELEC_RATE * 30).toFixed(2)} €/mois
                      </CostSub>
                    </CostCard>
                  );
                })}
                {costToggles.forfaits && (
                  <CostCard>
                    <CostLabel>📡 Forfaits mobiles ({restarterCount} restarters)</CostLabel>
                    <CostValue>{dailyCostInternet.toFixed(2)} €/jour</CostValue>
                    <CostSub>{restarterCount} restarter{restarterCount > 1 ? 's' : ''} × {FORFAIT_MOBILE}€ = {monthlyInternet}€/mois</CostSub>
                  </CostCard>
                )}
                <CostCard style={{ borderColor: 'var(--primary)' }}>
                  <CostLabel>💰 Total mensuel estimé</CostLabel>
                  <CostValue $color="var(--primary)">{monthlyCost.toFixed(2)} €</CostValue>
                  <CostSub>
                    Élec: {monthlyCostElec.toFixed(2)}€ + Forfaits: {monthlyInternet}€
                    <br />{dailyTotalKwh.toFixed(3)} kWh/jour · {(dailyTotalKwh * 30).toFixed(1)} kWh/mois
                  </CostSub>
                </CostCard>
              </CostGrid>
            </div>
          </TwoColInner>

          {/* Projection des coûts */}
          <SubSectionLabel><Clock size={12} /> Projection des coûts dans le temps</SubSectionLabel>
          <CostGrid>
            {[
              { label: '1 semaine', days: 7 },
              { label: '1 mois', days: 30 },
              { label: '3 mois', days: 90 },
              { label: '6 mois', days: 180 },
              { label: '1 an', days: 365 },
            ].map(p => {
              const elec = dailyCostElec * p.days;
              const internet = (monthlyInternet / 30) * p.days;
              const total = elec + internet;
              return (
                <CostCard key={p.days}>
                  <CostLabel>{p.label}</CostLabel>
                  <CostValue $color={total > 100 ? '#ef4444' : total > 30 ? '#f59e0b' : 'var(--text-primary)'}>{total.toFixed(2)} €</CostValue>
                  <CostSub>
                    Élec: {elec.toFixed(2)}€ · Forfaits: {internet.toFixed(2)}€
                    <br />{(dailyTotalKwh * p.days).toFixed(1)} kWh consommés
                  </CostSub>
                </CostCard>
              );
            })}
          </CostGrid>

        </SectionPanelBody>
      </SectionPanel>
    </Page>
  );
};

export default ControlCenter;
